import React from 'react';
import { BsArrowLeft } from 'react-icons/bs';
import classNames from 'classnames';

import Modal, { ModalType } from 'legacy/common/components/modal/Modal';
import { useUpdateRequestApprovalMutation } from 'legacy/events/hooks';
import { showError, showToast } from 'legacy/common/utils/toast';
import { TierApproval } from 'legacy/events/types';
import FadeIn from 'legacy/common/components/transition/FadeIn';

interface RequestListDetailModalProps {
  eventId: string;
  request: TierApproval;
  onClose: () => void;
  onSkip: () => void;
}

const RequestListDetailModal: React.FC<RequestListDetailModalProps> = ({
  eventId,
  request,
  onClose,
  onSkip
}) => {
  const updateRequestApprovalMutation = useUpdateRequestApprovalMutation();

  // Mark as read when skipped.
  const skip = () => {
    updateRequestApprovalMutation.mutate({
      approvalId: request.id,
      eventId,
      status: request.status,
      readStatus: 'read'
    });

    onSkip();
  };

  const onClickReject = (request: TierApproval) => {
    showToast({
      icon: '👎',
      message: `${request.user?.firstName} ${request.user?.lastName} was denied approval`
    });

    updateRequestApprovalMutation.mutate(
      {
        approvalId: request.id,
        eventId,
        status: 'rejected',
        readStatus: 'read'
      },
      {
        onError: (error) => {
          showError({ title: 'Error', message: error.message });
        }
      }
    );

    setTimeout(() => {
      onSkip();
    }, 150);
  };

  const onClickApprove = (request: TierApproval) => {
    showToast({
      icon: '👍',
      message: `${request.user?.firstName} ${request.user?.lastName} was approved`
    });

    updateRequestApprovalMutation.mutate(
      {
        approvalId: request.id,
        eventId,
        status: 'approved',
        readStatus: 'read'
      },
      {
        onError: (error) => {
          showError({ title: 'Error', message: error.message });
        }
      }
    );

    setTimeout(() => {
      onSkip();
    }, 150);
  };

  return (
    <Modal
      type={ModalType.SMALL}
      visible
      renderFooter={() => (
        <div className="flex items-center justify-center gap-4">
          <button
            type="button"
            className={classNames(
              'w-12 h-12 rounded-full p-1 border-1 border-primary-50 text-base text-center',
              {
                'bg-gray-500': request?.status === 'rejected'
              }
            )}
            onClick={() => onClickReject(request)}
          >
            👎
          </button>

          <button
            type="button"
            className={classNames(
              'w-12 h-12 rounded-full p-1 border-1 border-primary-50 text-base text-center'
            )}
            onClick={skip}
          >
            ⏭️
          </button>

          <button
            type="button"
            className={classNames(
              'w-12 h-12 rounded-full p-1 border-1 border-primary-50 text-base text-center',
              {
                'bg-gray-500': request?.status === 'approved'
              }
            )}
            onClick={() => onClickApprove(request)}
          >
            👍
          </button>
        </div>
      )}
    >
      <div className="relative overflow-auto" key={request.id}>
        <div
          className="absolute top-4 left-2 h-7 w-7 bg-gray-100 z-30 rounded-full flex items-center justify-center cursor-pointer"
          onClick={onClose}
        >
          <BsArrowLeft />
        </div>

        <FadeIn>
          <div className="absolute top-0 left-0 h-24 w-full bg-linear-to-r from-[#D0CEFF] to-[#C4DDFF] z-10" />

          <div className="relative pt-11 mx-auto flex flex-col items-center justify-center z-20">
            <div className="w-24 h-24 object-cover rounded-full border-2 border-primary-300 overflow-hidden align-center justify-center flex">
              <img
                src={request?.user?.photoUrl || '/img/search/user.svg'}
                className="w-full h-full"
              />
            </div>

            <div className="text-2xl mt-4 font-semibold">
              {request?.user?.firstName} {request?.user?.lastName}
            </div>
          </div>

          <div className="p-8">
            <div className="border-b-1 border-gray-100" />
            <p className="text-lg pt-4 pb-2 text-center">🎟️</p>
            <p className="text-lg font-bold pb-4 text-center">
              {request?.tier?.name}
            </p>

            <div className="border-b-1 border-gray-100" />
          </div>

          <div className="pt- px-8">
            {request?.answers?.map((answer) => (
              <div className="pb-8" key={answer.questionId}>
                <p className="text-base font-semibold">{answer?.question}</p>
                <p className="text-base font-normal">{answer?.answer}</p>
              </div>
            ))}
          </div>
        </FadeIn>
      </div>
    </Modal>
  );
};

export default RequestListDetailModal;
